/* eslint-disable max-len */
import type { NextPage, GetStaticProps, GetStaticPaths } from 'next';
import * as Sentry from '@sentry/nextjs';
import { publishMode, revalidate, domain, fallback } from '@/constants/config';
import type { PageProps, QueryData, AllPageData } from '@/types/index';
import DefaultErrorPage from 'next/error';
import { useIsPreviewing, BuilderComponent } from '@builder.io/react';
import { useEffect } from 'react';
import { init } from '@feathery/react';
import { restRequest } from '@/utils/restRequest';
import { useColorProvider } from '@/utils/ColorProvider';
import PageHead from '@/components/Head';
import Footer from '@/components/Footer';
import { usePostHog } from 'posthog-js/react';
import { GeneralError } from '@/types/errors';

// set revalidate from env. This determines how long the pages will cache and
// therefore how long changes will take to appear

export const getStaticProps: GetStaticProps = async ({
  params
}) => {
  let pageArr: Array<object>;
  let duplicatePaths: Array<string> = [];
  const urlPath = Array.isArray(params?.page) ? `/${params.page.join('/') || ''}` : `/${params?.page || ''}`;
  try {
    if (!!publishMode) {
      // Pull page specific to env domain if in published mode
      // Then filter out correct page by its publishedUrl
      const domainPagesArr = await restRequest<QueryData<'page'>[]>('custom-page', `data.domain.id=${domain}`);
      pageArr = domainPagesArr?.filter(page => page.data?.publishedUrl === urlPath);

      // check for duplicate paths for more visible error in publishMode
      const paths = domainPagesArr.map(item => item.data?.publishedUrl).filter((path): path is string => path !== undefined);
      duplicatePaths = paths.filter((item, index) => paths.indexOf(item) !== index);
    } else {
      pageArr = await restRequest<QueryData<'page'>[]>('custom-page', undefined, `urlPath=${urlPath}`);
    }
  } catch (err) {
    // error bubbled up from restRequest
    Sentry.captureException(err);
    pageArr = [];
  }
  const page = pageArr?.length && pageArr.length > 0 ? pageArr[0] : null;
  try {
    const formsData = await restRequest<QueryData<'data', {
      formId: string;
    }>[]>('form');
    const forms = formsData.map(formData => formData?.data?.formId);
    const filteredForms = forms.filter(id => id !== '<form-id>');
    return {
      props: {
        content: {
          page
        },
        duplicate: duplicatePaths,
        filteredForms
      },
      revalidate
    };
  } catch (err) {
    // error bubbled up from restRequest
    Sentry.captureException(err);
    return {
      props: {
        content: {
          page: null
        },
        duplicate: duplicatePaths,
        filteredForms: []
      },
      revalidate
    };
  }
};

/**
 * @note Using alias to match data definition
 */
export const getStaticPaths: GetStaticPaths = async () => {
  let pagesArr;
  if (publishMode) {
    // Pull pages specific to env domain if in published mode
    // When published we use publishedUrl rather than standard url
    try {
      pagesArr = await restRequest<AllPageData>('custom-page', `data.domain.id=${domain}`, undefined, 'data.publishedUrl,name,id');
      const publishedPages = pagesArr?.filter(page => !!page.data?.publishedUrl);
      const publishedPathsArr = publishedPages?.map(page => `${page.data?.publishedUrl}`);
      return {
        paths: publishedPathsArr,
        fallback: false
      };
    } catch (err) {
      // error bubbled up from restRequest
      Sentry.captureException(err);
      return {
        paths: [],
        fallback: false
      };
    }
  }
  // Pull all page urls

  try {
    pagesArr = await restRequest<AllPageData>('custom-page', undefined, undefined, 'data,name,id');
    const pagePaths = pagesArr?.filter(page => !!page.data?.url && page.data?.url !== '/404');
    return {
      paths: pagePaths?.map(page => `${page.data?.url}`) || [],
      fallback
    };
  } catch (err) {
    // error bubbled up from restRequest
    Sentry.captureException(err);
    return {
      paths: [],
      fallback
    };
  }
};
export const Page: NextPage<PageProps> = ({
  content,
  duplicate,
  forms
}) => {
  const sdk = process.env.NEXT_PUBLIC_FEATHERY_SDK;
  const sdkExists = typeof sdk === 'string';
  if (sdkExists) {
    init(sdk || '', {
      preloadForms: forms || []
    });
  }
  const isPreviewing = useIsPreviewing();
  const posthog = usePostHog();
  useEffect(() => {
    // Track page details when component mounts
    posthog?.capture('page_viewed', {
      title: content?.page?.data?.title,
      url: content?.page?.data?.url,
      isPreview: isPreviewing,
      hasFooter: !!content?.page?.data?.footer,
      formCount: forms?.length || 0
    });
  }, [content, isPreviewing, forms, posthog]);

  // visibly error if we have duplicate slugs. Should only happen in
  // publish mode
  if (duplicate.length > 0) {
    Sentry.withScope(scope => {
      scope.setLevel('warning');
      scope.setContext('duplicates', {
        paths: JSON.stringify(duplicate),
        currentPath: content?.page?.data?.url,
        currentTitle: content?.page?.data?.title
      });
      Sentry.captureMessage(`Duplicate published paths detected: ${duplicate.join(', ')}`);
    });

    // eslint-disable-next-line no-console
    console.warn('Duplicate published paths detected. This may cause issues with SEO and display the wrong page:', JSON.stringify(duplicate));
  }

  // 404 if no page found
  if (!content || !content.page && !isPreviewing) {
    if (process.env.VERCEL_ENV === 'production') {
      Sentry.captureMessage(GeneralError.NoPageFound, {
        level: 'error'
      });
    }
    return <DefaultErrorPage statusCode={404} />;
  }
  const {
    title,
    description,
    shareImage,
    url,
    primaryAccentColor,
    secondaryAccentColor,
    footer,
    isLightMode,
    footerBackgroundColor,
    footerTextColor
  } = content.page?.data || {};
  const {
    setColors
  } = useColorProvider();
  useEffect(() => {
    if (primaryAccentColor || secondaryAccentColor) {
      setColors({
        primaryAccentColor,
        secondaryAccentColor
      });
    }
  }, []);
  return <>
			<PageHead path={url || '/'} title={title || ''} description={description || ''} shareImage={shareImage} data-sentry-element="PageHead" data-sentry-source-file="[[...page]].tsx" />
			<BuilderComponent content={content.page} model="custom-page" options={{
      includeRefs: true
    }} data-sentry-element="BuilderComponent" data-sentry-source-file="[[...page]].tsx" />
			<Footer isLightMode={isLightMode} copy={footer?.value.data.copy || ''} links={footer?.value.data.links || []} footerBackgroundColor={footerBackgroundColor} footerTextColor={footerTextColor} data-sentry-element="Footer" data-sentry-source-file="[[...page]].tsx" />
		</>;
};
export default Page;